/**
 * Feed job-card badge row — one arm, not three.
 *
 * The row carries up to five chips. An audit on 2026-09-07 measured what they
 * are worth and found that three of the four badges under review had no
 * telemetry at all (closed by #5102, which added `BadgeMeta`), and that two of
 * them fire on almost every row:
 *
 *  - `urgency-chip` rendered on 5,652 of 5,652 live jobs, and 98.7% of those
 *    countdowns came from `expires_at_source = 'inferred'` — our own default
 *    window, not an employer deadline. That is a false claim, not a weak one,
 *    so it is fixed OUTSIDE this experiment: the chip now renders only for the
 *    ~1.3% of jobs carrying a real deadline, on every arm including control.
 *    You do not A/B whether to stop telling users something untrue.
 *  - `hidden-gem-pill` fired on 67% of live jobs.
 *
 * What IS a preference, and therefore what this flag tests, is the shape of
 * the row once the false claim is gone:
 *
 *  1. one chip budget for both layouts (desktop was uncapped and also skipped
 *     the "exactly one fit chip" rule, which is how a single card showed the
 *     fit pill, the hidden-gem pill, the urgency chip and the skills chip at
 *     once);
 *  2. one scarcity slot — a real deadline OR low competition, never both;
 *  3. one chromatic voice — the fit chip is the only tinted chip, per
 *     DESIGN.md's One Voice Rule (the row shipped four hues, two of them raw
 *     Tailwind palette utilities);
 *  4. `isFirstMoverGem` instead of `isHiddenGem` for the badge, taking the
 *     "be the first to apply" claim from 67% of live jobs to ~14.5%.
 *
 * WHY ONE ARM AND NOT FOUR. The feed sees ~19 people and ~552 unique
 * person-job impression pairs per day. At a 2.59% card click rate, detecting a
 * 25% relative lift two-sided at 80% power needs ~21,000 pairs — 38 days per
 * comparison, run serially against the same traffic, so four isolated tests is
 * roughly 150 days to learn anything. Bundling them into a single row-level
 * treatment costs the ability to attribute the win to one chip, and buys a
 * readout in ~13-16 days. Under PMF mode the row is the right unit anyway: the
 * question is whether this card earns more applies, not which pill did it.
 *
 * Those 16,574 pairs are also clustered inside ~440 people, so the fixed-n
 * figures above are optimistic rather than conservative. Treat a null result
 * as "too small to see here", never as "no effect".
 *
 * Decision criteria — write the readout date down before flipping this on:
 *  - Primary metric: `job_card_clicked` per `feed_card_impression`, one-sided
 *    (2.59% baseline; the hypothesis is directional — a quieter, truer row
 *    should not read worse).
 *  - Secondary, reported but not deciding: `job_apply_outbound_clicked` per
 *    impression (0.97% baseline, ~160/30d — too thin to decide on).
 *  - Guardrail: card CTR must not fall more than 15% relative. The urgency fix
 *    ships on both arms, so a drop there is a row-shape effect, not the
 *    truth fix.
 *  - Minimum run: 13 days AND >= 6,900 impression pairs across both arms.
 *    Do not read it early; at this volume an early peek is noise.
 *
 * PostHog auto-tags every event captured after `getFeatureFlag()` resolves
 * with `$feature/feed-badge-row`, so `feed_card_impression`,
 * `job_card_clicked` and `job_apply_outbound_clicked` are all analyzable by
 * arm with no extra plumbing — the same mechanism `apply-guest-no-signup.ts`
 * and `gate-cta-value-prop.ts` already rely on. The `BadgeMeta` fields from
 * #5102 say WHICH badges rendered within each arm.
 */

export const FEED_BADGE_ROW_FLAG = "feed-badge-row";

export type FeedBadgeRowArm = "control" | "v2";

/**
 * Resolve the arm from whatever `posthog.getFeatureFlag()` returned.
 *
 * Anything that is not the exact string `"v2"` resolves to `control`: an
 * unresolved flag (`undefined` while `/decide` is still in flight), a missing
 * flag, a disabled flag, and a boolean `true` from a flag someone converted
 * from multivariate all mean "render today's row". A multivariate flag read as
 * a boolean is a real bug this repo has already paid for once, so the coercion
 * is written down here rather than left to a truthiness check at the call
 * site.
 *
 * Control is the safe-off state on purpose. If this resolved to `v2` before
 * the flag loaded, every viewer would see the treatment for the first frames
 * regardless of their assigned arm, and both arms would be contaminated.
 */
export function resolveFeedBadgeRowArm(
	raw: string | boolean | undefined | null,
): FeedBadgeRowArm {
	return raw === "v2" ? "v2" : "control";
}
